/**
 * @module headerMappingBuilder
 *
 * Header mapping builder utilities for bridging file headers to app field names.
 *
 * **Problem solved:** Uploaded Excel/CSV files use inconsistent column headers --
 * "Loan Name", "LOAN_NAME", "loan-name", "Loan  Name" all mean the same field.
 * These utilities build a lookup table that maps all known variations to the
 * canonical UPPER_CASE field name used by the application.
 *
 * **Architecture role:** This module produces mapping objects that are consumed by
 * `createColumnNormalizer()` from `columnMappings.js`. The normalizer is then
 * passed to `useFileUploadProcessor` or `useUploadConfig` as `columnNormalizer`.
 *
 * **Key convention:** All mapping keys are stored in **lowercase-with-spaces**
 * format (e.g., `'loan name'`). This matches the internal normalization that
 * `createColumnNormalizer` applies to incoming column names before lookup.
 * The mapping values are the canonical **UPPER_CASE_WITH_UNDERSCORES** field
 * names (e.g., `'LOAN_NAME'`).
 *
 * **Integration flow:**
 * ```
 * fieldDefs (JSON) ──> buildHeaderMappings() ──> mappings object
 *                                                      │
 *                              createColumnNormalizer(mappings) ──> normalizer function
 *                                                                        │
 *                                              useUploadConfig({ columnNormalizer }) or
 *                                              useFileUploadProcessor({ columnNormalizer })
 * ```
 *
 * Works in conjunction with `createColumnNormalizer` from `columnMappings.js`.
 *
 * @example <caption>Full pipeline from fieldDefs to normalizer</caption>
 * import { buildHeaderMappings } from '@1823-partners/core';
 * import { createColumnNormalizer } from '@1823-partners/core';
 * import creFields from '../data/creFields.json';
 *
 * const mappings = buildHeaderMappings(creFields);
 * const normalizer = createColumnNormalizer(mappings);
 *
 * normalizer('Loan Name');    // 'LOAN_NAME'
 * normalizer('loan_name');    // 'LOAN_NAME'
 * normalizer('LOAN-NAME');    // 'LOAN_NAME'
 * normalizer('Unknown Col');  // 'Unknown Col' (returned as-is if no mapping)
 */

/**
 * Normalizes a header name to UPPER_CASE_WITH_UNDERSCORES (the PAM app convention).
 *
 * This is a standalone utility for when you need to normalize a single header
 * to the canonical format without building a full mapping table. It does NOT
 * look up aliases -- it simply applies formatting rules.
 *
 * Rules applied:
 * 1. Trims surrounding whitespace
 * 2. Replaces runs of spaces, underscores, and hyphens with a single underscore
 * 3. Uppercases the result
 *
 * @param {string} name - The header name to normalize
 * @returns {string} The normalized UPPER_CASE name, or `''` if name is falsy
 *
 * @example
 * normalizeHeaderToUpperCase('Loan Name');       // 'LOAN_NAME'
 * normalizeHeaderToUpperCase('loan-name');        // 'LOAN_NAME'
 * normalizeHeaderToUpperCase('  LOAN_NAME  ');    // 'LOAN_NAME'
 * normalizeHeaderToUpperCase('loan  name');       // 'LOAN_NAME'
 * normalizeHeaderToUpperCase('property--type');    // 'PROPERTY_TYPE'
 * normalizeHeaderToUpperCase('');                  // ''
 * normalizeHeaderToUpperCase(null);                // ''
 */
export const normalizeHeaderToUpperCase = (name) => {
  if (!name && name !== 0) return '';
  return name
    .toString()
    .trim()
    .replace(/[\s_-]+/g, '_')
    .toUpperCase();
};

/**
 * Converts a raw string to the lowercase-with-spaces key format used by
 * `createColumnNormalizer`. This mirrors the normalization that
 * `createColumnNormalizer` applies to incoming column names before looking
 * them up in the mappings object.
 *
 * The key format is: lowercase, trimmed, all separators (underscores, hyphens,
 * multiple spaces) collapsed to single spaces.
 *
 * @param {string} name - Raw header string
 * @returns {string} Lowercase, space-separated key (e.g., `'loan name'`)
 * @private
 */
const toLookupKey = (name) => {
  if (!name && name !== 0) return '';
  return name
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[_\s-]+/g, ' ')
    .replace(/\s+/g, ' ');
};

/**
 * Builds column mappings from a field definitions object (like CRE's `creFields.json`).
 *
 * For each field definition, this function generates multiple lookup entries so
 * that various header spellings all resolve to the same canonical field name:
 *
 * 1. **Field key itself** (e.g., `LOAN_NAME` -> lookup key `'loan name'` -> target `'LOAN_NAME'`)
 * 2. **Display name** from `def.name` (e.g., `'Loan Name'` -> lookup key `'loan name'` -> target `'LOAN_NAME'`)
 * 3. **Each alias** from `def.aliases[]` (e.g., `'loan'` -> lookup key `'loan'` -> target `'LOAN_NAME'`)
 *
 * The `extraAliases` parameter is a flat `{ SOURCE: TARGET }` map merged last,
 * so its entries override any auto-generated mappings for the same lookup key.
 * This is useful for legacy column names that do not fit into the fieldDefs structure.
 *
 * All returned keys use lowercase-with-spaces format to match
 * `createColumnNormalizer`'s internal normalization.
 *
 * @param {Object<string, {name: string, aliases?: string[]}>} fieldDefs - Field
 *   definitions keyed by target field name (UPPER_CASE). Each value should have:
 *   - `name` {string} - Human-readable display name (e.g., `'Loan Name'`)
 *   - `aliases` {string[]} (optional) - Additional header strings that map to this field
 * @param {Object<string, string>} [extraAliases={}] - Flat map of
 *   `{ 'SOURCE_HEADER': 'TARGET_FIELD' }` for legacy or one-off aliases.
 *   Later entries win over earlier ones for duplicate keys.
 * @returns {Object<string, string>} Mapping object suitable for
 *   `createColumnNormalizer(mappings)`. Keys are lowercase-with-spaces lookup
 *   strings; values are UPPER_CASE canonical field names.
 *
 * @example <caption>From a fieldDefs JSON file</caption>
 * import { buildHeaderMappings } from '@1823-partners/core';
 *
 * const fieldDefs = {
 *   LOAN_NAME: { name: 'Loan Name', aliases: ['loan', 'name of loan'] },
 *   CUSIP:     { name: 'CUSIP' },
 *   MATURITY_DATE: { name: 'Maturity Date', aliases: ['maturity', 'mat date'] },
 * };
 *
 * const mappings = buildHeaderMappings(fieldDefs);
 * // Result:
 * // {
 * //   'loan name': 'LOAN_NAME',      // from field key and display name
 * //   'loan': 'LOAN_NAME',           // from alias
 * //   'name of loan': 'LOAN_NAME',   // from alias
 * //   'cusip': 'CUSIP',              // from field key and display name
 * //   'maturity date': 'MATURITY_DATE',
 * //   'maturity': 'MATURITY_DATE',
 * //   'mat date': 'MATURITY_DATE',
 * // }
 *
 * @example <caption>With extraAliases for legacy headers</caption>
 * const mappings = buildHeaderMappings(fieldDefs, {
 *   'Old Loan Col': 'LOAN_NAME',   // legacy spreadsheet uses this header
 *   'MAT_DT': 'MATURITY_DATE',     // abbreviated header from old system
 * });
 * // Extra aliases are normalized and merged:
 * // { ...auto-generated..., 'old loan col': 'LOAN_NAME', 'mat dt': 'MATURITY_DATE' }
 */
export const buildHeaderMappings = (fieldDefs, extraAliases = {}) => {
  const mappings = {};

  Object.entries(fieldDefs).forEach(([targetField, def]) => {
    // Map from the normalized field key (e.g. 'LOAN_NAME' -> lookup key 'loan name')
    const keyFromField = toLookupKey(targetField);
    if (keyFromField) {
      mappings[keyFromField] = targetField;
    }

    // Map from the normalized display name
    if (def && def.name) {
      const keyFromName = toLookupKey(def.name);
      if (keyFromName) {
        mappings[keyFromName] = targetField;
      }
    }

    // Map from each alias
    if (def && Array.isArray(def.aliases)) {
      def.aliases.forEach((alias) => {
        const keyFromAlias = toLookupKey(alias);
        if (keyFromAlias) {
          mappings[keyFromAlias] = targetField;
        }
      });
    }
  });

  // Merge extra aliases last -- they override any previously generated mappings
  Object.entries(extraAliases).forEach(([source, target]) => {
    const key = toLookupKey(source);
    if (key) {
      mappings[key] = target;
    }
  });

  return mappings;
};

/**
 * Builds mappings from a simple alias structure (blotter-style).
 *
 * This is a simpler alternative to `buildHeaderMappings` for apps that define
 * their own alias arrays instead of using a fieldDefs JSON file. Each key in
 * `aliasMap` is the canonical target field name; the value is an array of all
 * known header strings that should map to it.
 *
 * All aliases are normalized to lowercase-with-spaces lookup keys, so the
 * result is directly compatible with `createColumnNormalizer`.
 *
 * @param {Object<string, string[]>} aliasMap - Map of target field names to
 *   arrays of alias strings. Each alias can use any case/separator style --
 *   it will be normalized during lookup.
 * @returns {Object<string, string>} Mapping object suitable for
 *   `createColumnNormalizer(mappings)`.
 *
 * @example <caption>Blotter-style alias definitions</caption>
 * import { buildHeaderMappingsFromAliases } from '@1823-partners/core';
 * import { createColumnNormalizer } from '@1823-partners/core';
 *
 * const aliasMap = {
 *   CUSIP: ['CUSIP', 'cusip', 'Cusip', 'CUSIP_ID'],
 *   WAL_CURR: ['WAL', 'WAL_CURR', 'Weighted Average Life', 'Current WAL'],
 *   BOOK_YIELD: ['Book Yield', 'BOOK_YIELD', 'BK_YLD', 'Yield'],
 * };
 *
 * const mappings = buildHeaderMappingsFromAliases(aliasMap);
 * // Result:
 * // {
 * //   'cusip': 'CUSIP',
 * //   'cusip id': 'CUSIP',
 * //   'wal': 'WAL_CURR',
 * //   'wal curr': 'WAL_CURR',
 * //   'weighted average life': 'WAL_CURR',
 * //   'current wal': 'WAL_CURR',
 * //   'book yield': 'BOOK_YIELD',
 * //   'bk yld': 'BOOK_YIELD',
 * //   'yield': 'BOOK_YIELD',
 * // }
 *
 * // Then create a normalizer:
 * const normalizer = createColumnNormalizer(mappings);
 * normalizer('Weighted_Average_Life');  // 'WAL_CURR'
 * normalizer('BK YLD');                 // 'BOOK_YIELD'
 */
export const buildHeaderMappingsFromAliases = (aliasMap) => {
  const mappings = {};

  Object.entries(aliasMap).forEach(([targetField, aliases]) => {
    if (!Array.isArray(aliases)) return;
    aliases.forEach((alias) => {
      const key = toLookupKey(alias);
      if (key) {
        mappings[key] = targetField;
      }
    });
  });

  return mappings;
};

const headerMappingBuilder = {
  normalizeHeaderToUpperCase,
  buildHeaderMappings,
  buildHeaderMappingsFromAliases,
};

export default headerMappingBuilder;
